/**
 * Normalize Strapi REST / Document shapes for string-registry template + entries
 * (relations often arrive as `{ data: ... }`; locale rows may be nested).
 */

import type { StringRegistryItem, StringTemplate, StringTemplateItem } from "../types/stringRegistryTypes";

function unwrapEntity(node: unknown): any {
  if (node == null || typeof node !== "object") return node;
  const o = node as Record<string, unknown>;
  if (o.data !== undefined) {
    return unwrapEntity(o.data);
  }
  return node;
}

function asRelationArray(node: unknown): any[] {
  if (node == null) return [];
  if (Array.isArray(node)) return node;
  if (typeof node === "object" && node !== null) {
    const d = (node as { data?: unknown }).data;
    if (Array.isArray(d)) return d;
    if (d != null) return [d];
  }
  return [];
}

/** Strapi v4-style attributes bag → flat document */
function flattenAttributes(doc: unknown): any {
  const d = unwrapEntity(doc);
  if (d == null || typeof d !== "object") return d;
  const o = d as Record<string, unknown>;
  if (o.attributes != null && typeof o.attributes === "object") {
    return { documentId: o.documentId, id: o.id, ...(o.attributes as object) };
  }
  return d;
}

export function normalizeStringRegistryEntryFromStrapi(raw: unknown): StringRegistryItem | undefined {
  const doc = flattenAttributes(raw);
  if (!doc || typeof doc !== "object") return undefined;
  const documentId = String((doc as StringRegistryItem).documentId ?? "");
  if (!documentId) return undefined;
  const templateItems = asRelationArray((doc as any).templateItems).map((row) => flattenAttributes(row));

  const lookups = asRelationArray((doc as any).lookups).map((x) => flattenAttributes(x));

  const components = lookups
    .filter((l: any) => l?.lookupType?.code === "COMPONENT")
    .map((l: any) => ({
      documentId: l.documentId,
      code: l.code,
      displayName: l.displayName
    }));
  const sections = lookups
    .filter((l: any) => l?.lookupType?.code === "SECTION")
    .map((l: any) => ({
      documentId: l.documentId,
      code: l.code,
      displayName: l.displayName
    }));

  return {
    documentId,
    stringResourceId: String((doc as StringRegistryItem).stringResourceId ?? ""),
    displayName: String((doc as StringRegistryItem).displayName ?? ""),
    documentation: String((doc as StringRegistryItem).documentation ?? ""),
    verificationStatus: String((doc as StringRegistryItem).verificationStatus ?? "TODO"),
    deprecated: Boolean((doc as StringRegistryItem).deprecated),
    components,
    sections,
    templateItems: templateItems as StringRegistryItem['templateItems']
  };
}

function normalizeLocaleOverrides(raw: unknown): { locale: string; value: string }[] {
  const rows = Array.isArray(raw) ? raw : asRelationArray(raw);
  return rows
    .map((row) => flattenAttributes(row))
    .filter(Boolean)
    .map((r) => ({
      locale: String(r.locale ?? ""),
      value: String(r.value ?? "")
    }))
    .filter((r) => r.locale);
}

export function normalizeStringTemplateItemFromStrapi(item: unknown): StringTemplateItem {
  const it = flattenAttributes(item) as StringTemplateItem;
  return {
    ...it,
    stringRegistryEntry: normalizeStringRegistryEntryFromStrapi((it as any).stringRegistryEntry),
    localeOverrides: normalizeLocaleOverrides((it as any).localeOverrides)
  };
}

export function normalizeStringRegistryTemplateFromStrapi(raw: unknown): StringTemplate {
  const t = flattenAttributes(raw) as StringTemplate;
  const items = (t.items || []).map((i) => normalizeStringTemplateItemFromStrapi(i));
  return { ...t, items };
}
